

import React, { useState, useEffect } from 'react';
import JsonView from '@uiw/react-json-view';

function JsonViewer() {
  const [jsonText, setJsonText] = useState('');
  const [parsedJson, setParsedJson] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [jsonKey, setJsonKey] = useState(0);
  const [expandAll, setExpandAll] = useState(true);
  const [matches, setMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);

  // Compare two paths
  const arraysEqual = (a, b) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  // Highlight matching substring
  const highlightSearch = (text) => {
    if (!searchTerm || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} style={{ backgroundColor: 'yellow', padding: 0 }}>
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  // Build matches array: search both keys and string values
  const updateMatches = (data, term) => {
    if (!term) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      return;
    }
    const lower = term.toLowerCase();
    const found = [];

    const walk = (obj, path = []) => {
      // value match
      if (typeof obj === 'string' && obj.toLowerCase().includes(lower)) {
        found.push({ type: 'value', path, text: obj });
      }
      // object: check keys and recurse
      if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach((key) => {
          if (key.toLowerCase().includes(lower)) {
            found.push({ type: 'key', path: [...path, key], text: key });
          }
          walk(obj[key], [...path, key]);
        });
      }
    };

    if (data) walk(data, []);
    console.log('🔍 Found matches:', found);
    setMatches(found);
    setCurrentMatchIndex(found.length > 0 ? 0 : -1);
  };

  // Whenever JSON or searchTerm change, recompute matches
  useEffect(() => {
    updateMatches(parsedJson, searchTerm);
    // bump jsonKey so JsonView re-renders and picks up new match IDs
    setJsonKey((k) => k + 1);
  }, [parsedJson, searchTerm]);

  // Auto-scroll to active match
  useEffect(() => {
    if (currentMatchIndex < 0) return;
    const el = document.getElementById(`match-${currentMatchIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMatchIndex, matches]);

  // Navigation handlers
  const nextMatch = () => {
    if (matches.length > 0) {
      setCurrentMatchIndex((idx) => (idx + 1) % matches.length);
    }
  };
  const prevMatch = () => {
    if (matches.length > 0) {
      setCurrentMatchIndex((idx) => (idx - 1 + matches.length) % matches.length);
    }
  };

  // Render a value: highlight and assign id if it's a match
  const renderValueRenderer = ({ value, namespace }) => {
    const idx = matches.findIndex(
      (m) => m.type === 'value' && arraysEqual(m.path, namespace)
    );
    if (idx !== -1) {
      const isActive = idx === currentMatchIndex;
      return (
        <span id={isActive ? `match-${idx}` : undefined}>
          {highlightSearch(value)}
        </span>
      );
    }
    return value;
  };

  // Render a key: highlight and assign id if it's a match
  const renderNameRenderer = ({ name, namespace }) => {
    const fullPath = [...namespace, name];
    const idx = matches.findIndex(
      (m) => m.type === 'key' && arraysEqual(m.path, fullPath)
    );
    if (idx !== -1) {
      const isActive = idx === currentMatchIndex;
      return (
        <span id={isActive ? `match-${idx}` : undefined}>
          {highlightSearch(name)}
        </span>
      );
    }
    return name;
  };

  // Load JSON from URL param ?url=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const url = params.get('url');
    if (url) {
      setLoading(true);
      fetch(url)
        .then((res) => (!res.ok ? Promise.reject(res.status) : res.json()))
        .then((data) => {
          setParsedJson(data);
          setJsonText(JSON.stringify(data, null, 2));
          setError('');
        })
        .catch((err) => setError('⚠️ JSON load error: ' + err))
        .finally(() => setLoading(false));
    }
  }, []);

  // Handlers: parse, upload, download, copy, clear, toggle dark
  const handleParse = () => {
    try {
      const obj = JSON.parse(jsonText);
      setParsedJson(obj);
      setError('');
    } catch {
      setError('⚠️ Invalid JSON');
      setParsedJson(null);
    }
  };
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      setJsonText(text);
      try {
        const parsed = JSON.parse(text);
        setParsedJson(parsed);
        setError('');
      } catch {
        setError('⚠️ Invalid JSON in file');
        setParsedJson(null);
      }
    };
    reader.readAsText(file);
  };
  const handleDownload = () => {
    if (!parsedJson) return;
    const blob = new Blob([JSON.stringify(parsedJson, null, 2)], {
      type: 'application/json',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data.json';
    link.click();
  };
  const handleCopy = () =>
    parsedJson &&
    navigator.clipboard.writeText(JSON.stringify(parsedJson, null, 2));
  const handleClear = () => {
    setJsonText('');
    setParsedJson(null);
    setError('');
  };
  const toggleDark = () => setDarkMode((dm) => !dm);

  const bgPage = darkMode ? '#121212' : '#f5f7fa';
  const textColor = darkMode ? '#eee' : '#333';
  const bgContainer = darkMode ? '#222' : '#fff';

  return (
    <div
      style={{
        background: bgPage,
        color: textColor,
        minHeight: '100vh',
        padding: 20,
        fontFamily: 'Segoe UI, sans-serif',
        direction:'ltr'
      }}
    >
      <div
        style={{
          maxWidth: 900,
          margin: 'auto',
          background: bgContainer,
          borderRadius: 8,
          padding: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <h1 style={{ textAlign: 'center', marginBottom: 20 }}>🔍 JSON Viewer</h1>

        {/* Dark toggle */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            onClick={toggleDark}
            style={{
              padding: '10px 20px',
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {darkMode ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>

        {/* Import file */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <label
            style={{
              padding: '10px 20px',
              borderRadius: 4,
              background: darkMode ? '#556cd6' : '#007bff',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            📁 Import JSON
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {/* Textarea */}
        <textarea
          rows={6}
          style={{
            width: '100%',
            fontFamily: 'monospace',
            borderRadius: 6,
            padding: 12,
            border: '1px solid #ccc',
            background: darkMode ? '#333' : '#fafafa',
            color: textColor,
            marginBottom: 8,
          }}
          placeholder="Paste JSON or use Import..."
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
        />

        {/* Search + nav */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="🔎 Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flexGrow: 1,
              padding: '8px',
              borderRadius: 4,
              border: '1px solid #ccc',
              fontSize: 14,
            }}
          />
          <button onClick={prevMatch} style={{ padding: '0 12px' }}>
            ⬆️
          </button>
          <button onClick={nextMatch} style={{ padding: '0 12px' }}>
            ⬇️
          </button>
        </div>

        {/* Controls */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            justifyContent: 'center',
            marginBottom: 24,
          }}
        >
          <button onClick={handleParse} style={{ padding: '8px 16px' }}>
            📄 View
          </button>
          {parsedJson && (
            <>
              <button
                onClick={() => setExpandAll(true)}
                style={{ padding: '8px 16px' }}
              >
                ➕ Expand All
              </button>
              <button
                onClick={() => setExpandAll(false)}
                style={{ padding: '8px 16px' }}
              >
                ➖ Collapse All
              </button>
              <button onClick={handleDownload} style={{ padding: '8px 16px' }}>
                💾 Download
              </button>
              <button onClick={handleCopy} style={{ padding: '8px 16px' }}>
                📋 Copy
              </button>
              <button onClick={handleClear} style={{ padding: '8px 16px' }}>
                🧹 Clear
              </button>
            </>
          )}
        </div>

        {loading && <p style={{ textAlign: 'center' }}>🔄 Loading...</p>}
        {error && (
          <p style={{ textAlign: 'center', color: '#e55' }}>{error}</p>
        )}

        {/* JSON tree container */}
        {parsedJson && (
          <div
            style={{
              padding: 16,
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <JsonView
              key={jsonKey}
              value={parsedJson}
              collapsed={!expandAll}
              displayDataTypes={false}
              nameRenderer={renderNameRenderer}
              valueRenderer={renderValueRenderer}
            />
          </div>
        )}

        {/* Match indicator */}
        {matches.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            {`🔎 Match ${currentMatchIndex + 1} of ${matches.length}`}
          </div>
        )}
      </div>
    </div>
  );
}

export default JsonViewer;
